import { create } from 'zustand'
import { lastRequestId } from '@/shared/api/request-id'
import { buildErrorReport, classifyError, humanMessage } from '@/shared/lib/telemetry'

export type ToastItem = {
  id: string
  tone: 'error' | 'success'
  title: string
  description?: string
  requestId?: string | null
  /** Тот же отчёт, что копирует «экран смерти». Есть только у ошибок. */
  report?: string
}

type ToastState = {
  items: ToastItem[]
  dismiss: (id: string) => void
}

/**
 * Очередь тостов.
 *
 * Стор живёт в `shared/lib`, а не в виджете, потому что звать `toast.error()` должен код,
 * который к React отношения не имеет: глобальный обработчик ошибок мутаций сидит в
 * `MutationCache` (`app/providers/query-client.ts`), вне дерева компонентов. Zustand это умеет —
 * `getState()` работает откуда угодно.
 */
const useToastStore = create<ToastState>((set) => ({
  items: [],
  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}))

export const useToasts = () => useToastStore((state) => state.items)
export const useDismissToast = () => useToastStore((state) => state.dismiss)

function push(item: Omit<ToastItem, 'id'>): void {
  useToastStore.setState((state) => ({
    items: [...state.items, { ...item, id: crypto.randomUUID() }],
  }))
}

export const toast = {
  /**
   * Ошибка мутации. Текст берётся из той же классификации, что и «экран смерти», — значит
   * «нет связи», «сервер лёг» и «/api не проксируется» человек прочтёт одинаково и там, и тут,
   * а не двумя разными формулировками про одно и то же.
   *
   * `X-Request-Id` показывается строкой и уезжает в отчёт по кнопке «Скопировать»: без него
   * саппорт не найдёт запрос в логах шлюза.
   */
  /**
   * `say` — для случая, когда ВЫЗЫВАЮЩИЙ знает причину точнее классификатора. Классификация общая
   * (по виду сбоя), и на отказах она ставит диагноз по умолчанию: 4xx, кроме 403/404, читается как
   * баг приложения — «Что-то сломалось, обновите страницу». Для отказа «такая запись уже есть» это
   * и неверно, и вредно: чинится он не перезагрузкой, а тем, что список перечитан. Отчёт и
   * `X-Request-Id` при этом сохраняются — саппорту они нужны одинаково.
   */
  error(error: unknown, say?: { title?: string; description?: string }): void {
    const { title, hint } = humanMessage(classifyError(error))
    const requestId = lastRequestId()

    push({
      tone: 'error',
      title: say?.title ?? title,
      description: say?.description ?? hint,
      requestId,
      report: buildErrorReport({ error, requestId }),
    })
  },

  success(title: string, description?: string): void {
    push({ tone: 'success', title, description })
  },
}
